import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  Send,
  Check,
  Clock,
  Shield,
  MessageSquare,
} from "lucide-react";
import { config } from "@/config";

const interestOptions = [
  "Ecommerce",
  "Trading",
  "IA",
  "Finanzas",
  "Aún lo estoy evaluando",
];

const stageOptions = [
  "Solo explorando",
  "Quiero entender cómo funciona",
  "Ya decidí que quiero aprender",
  "Quiero que me contacten pronto",
];

const benefits = [
  { icon: Clock, text: "Respuesta en menos de 24 horas" },
  { icon: MessageSquare, text: "Orientación personalizada por DSA" },
  { icon: Shield, text: "Sin compromiso ni obligación" },
];

export default function LeadForm() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    interest: "",
    stage: "",
    goal: "",
    terms_accepted: false,
    contact_authorized: false,
  });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const errs = {};

    if (!formData.name.trim()) errs.name = "El nombre es obligatorio";
    if (!formData.phone.trim()) errs.phone = "El WhatsApp es obligatorio";

    if (!formData.email.trim()) {
      errs.email = "El correo es obligatorio";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errs.email = "Introduce un correo válido";
    }

    if (!formData.interest) errs.interest = "Selecciona tu interés";
    if (!formData.stage) errs.stage = "Selecciona tu etapa";

    if (!formData.terms_accepted) {
      errs.terms_accepted =
        "Debes aceptar los términos y la política de privacidad";
    }

    if (!formData.contact_authorized) {
      errs.contact_authorized = "Debes autorizar el contacto para continuar";
    }

    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errs = validate();
    setErrors(errs);

    if (Object.keys(errs).length > 0) return;

    setLoading(true);

    const urlParams = new URLSearchParams(window.location.search);

    const payload = {
      ...formData,
      whatsapp_opt_in: formData.contact_authorized,
      landing_entrada: "ecommerce",
      marca: "DSA",
      fuente_principal:
        urlParams.get("fuente_principal") || document.referrer || "",
      subfuente: urlParams.get("subfuente") || "",
      utm_source: urlParams.get("utm_source") || "",
      utm_medium: urlParams.get("utm_medium") || "",
      utm_campaign: urlParams.get("utm_campaign") || "",
      utm_content: urlParams.get("utm_content") || "",
      fecha_registro: new Date().toISOString(),
    };

    try {
      const res = await fetch(`${config.api.baseUrl}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        navigate("/gracias");
      } else {
        setErrors({ submit: "Hubo un error. Inténtalo de nuevo." });
      }
    } catch {
      setErrors({ submit: "Error de conexión. Inténtalo más tarde." });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <section
      id="formulario"
      data-testid="lead-form-section"
      className="py-20 md:py-28 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <div className="lg:sticky lg:top-32">
            <span className="text-sm uppercase tracking-[0.25em] font-semibold text-[#0F3D56] mb-4 block">
              Siguiente paso
            </span>

            <h2
              data-testid="form-title"
              className="text-3xl md:text-5xl font-semibold tracking-tight text-[#111827] mb-6"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Solicita información
            </h2>

            <p className="text-base text-gray-600 leading-relaxed mb-10">
              Completa el formulario y un asesor de DSA se comunicará contigo
              para orientarte sobre la propuesta y ayudarte a evaluar si encaja
              contigo.
            </p>

            <div className="space-y-5">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#0F3D56]/10 flex items-center justify-center flex-shrink-0">
                    <benefit.icon size={22} className="text-[#0F3D56]" />
                  </div>
                  <span className="text-sm text-gray-700">{benefit.text}</span>
                </div>
              ))}
            </div>

            <div className="mt-10 p-6 bg-gray-50 border border-gray-100">
              <p className="text-sm text-gray-500 leading-relaxed">
                <span className="font-semibold text-[#111827]">
                  Tu información está segura.
                </span>{" "}
                Los datos se usarán por DSA para contacto informativo,
                evaluación comercial, seguimiento y orientación sobre la oferta
                consultada.
              </p>
            </div>
          </div>

          <div>
            <form
              data-testid="lead-capture-form"
              onSubmit={handleSubmit}
              className="bg-white p-8 md:p-10 shadow-2xl border border-gray-100"
            >
              <div className="mb-8 pb-6 border-b border-gray-100">
                <h3 className="text-lg font-bold text-[#111827]">
                  Completa tus datos
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Todos los campos marcados son obligatorios
                </p>
              </div>

              <div className="space-y-5">
                <div>
                  <Label
                    htmlFor="name"
                    className="text-sm font-semibold text-[#111827] mb-2 block"
                  >
                    Nombre completo
                  </Label>
                  <Input
                    id="name"
                    data-testid="form-input-name"
                    type="text"
                    placeholder="Tu nombre completo"
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    className="h-12 bg-gray-50 border-gray-200 focus:border-[#0F3D56] focus:ring-1 focus:ring-[#0F3D56] rounded-none"
                  />
                  {errors.name && (
                    <p
                      data-testid="form-error-name"
                      className="text-sm text-red-500 mt-1"
                    >
                      {errors.name}
                    </p>
                  )}
                </div>

                <div>
                  <Label
                    htmlFor="phone"
                    className="text-sm font-semibold text-[#111827] mb-2 block"
                  >
                    WhatsApp
                  </Label>
                  <Input
                    id="phone"
                    data-testid="form-input-phone"
                    type="tel"
                    placeholder="+51 999 999 999"
                    value={formData.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    className="h-12 bg-gray-50 border-gray-200 focus:border-[#0F3D56] focus:ring-1 focus:ring-[#0F3D56] rounded-none"
                  />
                  {errors.phone && (
                    <p
                      data-testid="form-error-phone"
                      className="text-sm text-red-500 mt-1"
                    >
                      {errors.phone}
                    </p>
                  )}
                </div>

                <div>
                  <Label
                    htmlFor="email"
                    className="text-sm font-semibold text-[#111827] mb-2 block"
                  >
                    Correo electrónico
                  </Label>
                  <Input
                    id="email"
                    data-testid="form-input-email"
                    type="email"
                    placeholder="tu@email.com"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    className="h-12 bg-gray-50 border-gray-200 focus:border-[#0F3D56] focus:ring-1 focus:ring-[#0F3D56] rounded-none"
                  />
                  {errors.email && (
                    <p
                      data-testid="form-error-email"
                      className="text-sm text-red-500 mt-1"
                    >
                      {errors.email}
                    </p>
                  )}
                </div>

                <div>
                  <Label className="text-sm font-semibold text-[#111827] mb-2 block">
                    ¿Qué te interesa aprender?
                  </Label>
                  <Select
                    value={formData.interest}
                    onValueChange={(val) => handleChange("interest", val)}
                  >
                    <SelectTrigger
                      data-testid="form-select-interest"
                      className="h-12 bg-gray-50 border-gray-200 rounded-none focus:border-[#0F3D56] focus:ring-1 focus:ring-[#0F3D56]"
                    >
                      <SelectValue placeholder="Selecciona un área" />
                    </SelectTrigger>
                    <SelectContent>
                      {interestOptions.map((opt) => (
                        <SelectItem key={opt} value={opt}>
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.interest && (
                    <p
                      data-testid="form-error-interest"
                      className="text-sm text-red-500 mt-1"
                    >
                      {errors.interest}
                    </p>
                  )}
                </div>

                <div>
                  <Label className="text-sm font-semibold text-[#111827] mb-2 block">
                    ¿En qué etapa estás?
                  </Label>
                  <Select
                    value={formData.stage}
                    onValueChange={(val) => handleChange("stage", val)}
                  >
                    <SelectTrigger
                      data-testid="form-select-stage"
                      className="h-12 bg-gray-50 border-gray-200 rounded-none focus:border-[#0F3D56] focus:ring-1 focus:ring-[#0F3D56]"
                    >
                      <SelectValue placeholder="Selecciona tu etapa" />
                    </SelectTrigger>
                    <SelectContent>
                      {stageOptions.map((opt) => (
                        <SelectItem key={opt} value={opt}>
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.stage && (
                    <p
                      data-testid="form-error-stage"
                      className="text-sm text-red-500 mt-1"
                    >
                      {errors.stage}
                    </p>
                  )}
                </div>

                <div>
                  <Label
                    htmlFor="goal"
                    className="text-sm font-semibold text-[#111827] mb-2 block"
                  >
                    ¿Qué te gustaría lograr?
                    <span className="text-gray-400 font-normal ml-1">
                      (opcional)
                    </span>
                  </Label>
                  <Input
                    id="goal"
                    data-testid="form-input-goal"
                    type="text"
                    placeholder="Ej: Montar mi primera tienda online"
                    value={formData.goal}
                    onChange={(e) => handleChange("goal", e.target.value)}
                    className="h-12 bg-gray-50 border-gray-200 focus:border-[#0F3D56] focus:ring-1 focus:ring-[#0F3D56] rounded-none"
                  />
                </div>

                <div className="flex items-start gap-3 pt-2">
                  <button
                    type="button"
                    data-testid="form-terms-accepted"
                    onClick={() =>
                      handleChange(
                        "terms_accepted",
                        !formData.terms_accepted,
                      )
                    }
                    className={`flex-shrink-0 w-5 h-5 mt-0.5 border-2 flex items-center justify-center transition-all ${
                      formData.terms_accepted
                        ? "bg-[#0F3D56] border-[#0F3D56] text-white"
                        : "bg-white border-gray-300"
                    }`}
                    aria-label="Aceptar términos y privacidad"
                  >
                    {formData.terms_accepted && (
                      <Check size={12} strokeWidth={3} />
                    )}
                  </button>

                  <div className="text-sm text-gray-500 leading-relaxed">
                    He leído y acepto la{" "}
                    <a
                      href="/privacidad"
                      className="underline hover:text-[#0F3D56]"
                    >
                      Política de Privacidad
                    </a>{" "}
                    y los{" "}
                    <a
                      href="/terminos"
                      className="underline hover:text-[#0F3D56]"
                    >
                      Términos y Condiciones
                    </a>
                    .
                    {errors.terms_accepted && (
                      <p
                        data-testid="form-error-terms-accepted"
                        className="text-sm text-red-500 mt-1"
                      >
                        {errors.terms_accepted}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-3 pt-1">
                  <button
                    type="button"
                    data-testid="form-contact-authorized"
                    onClick={() =>
                      handleChange(
                        "contact_authorized",
                        !formData.contact_authorized,
                      )
                    }
                    className={`flex-shrink-0 w-5 h-5 mt-0.5 border-2 flex items-center justify-center transition-all ${
                      formData.contact_authorized
                        ? "bg-[#0F3D56] border-[#0F3D56] text-white"
                        : "bg-white border-gray-300"
                    }`}
                    aria-label="Autorizar contacto"
                  >
                    {formData.contact_authorized && (
                      <Check size={12} strokeWidth={3} />
                    )}
                  </button>

                  <div className="text-sm text-gray-500 leading-relaxed">
                    Autorizo a DSA a contactarme por WhatsApp y correo para
                    orientación, seguimiento comercial e información relacionada
                    con esta propuesta educativa.
                    {errors.contact_authorized && (
                      <p
                        data-testid="form-error-contact-authorized"
                        className="text-sm text-red-500 mt-1"
                      >
                        {errors.contact_authorized}
                      </p>
                    )}
                  </div>
                </div>

                {errors.submit && (
                  <p
                    data-testid="form-error-submit"
                    className="text-sm text-red-500 text-center py-2 bg-red-50 border border-red-100"
                  >
                    {errors.submit}
                  </p>
                )}

                <button
                  type="submit"
                  data-testid="form-submit-button"
                  disabled={loading}
                  className="w-full bg-[#0F3D56] text-white px-8 py-4 text-base font-bold hover:bg-[#0A2A3C] transition-all shadow-lg hover:shadow-xl tracking-wide disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-4"
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Solicitar Información
                    </>
                  )}
                </button>

                <p className="text-xs text-gray-400 text-center leading-relaxed pt-2">
                  Al enviar, tus datos serán utilizados por DSA para
                  orientación, evaluación comercial y seguimiento sobre la
                  propuesta consultada.
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
